import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { sourcesOf, searchOptions } from './getAsyncData';

// Status vem da taxonomia de tags do portal (mesma regra do ProcessSearch):
// lista FIXA, não inferida por substring — a versão antiga considerava
// "aberta" qualquer tag contendo 'andamento', o que era frágil.
const STATUS_TAGS = [
  ['Consulta Pública em Andamento', 'abertas', 'Aberta'],
  ['Consulta Pública Encerrada', 'encerradas', 'Encerrada'],
];

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');

const formatDate = (value) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

const statusOf = (item) => {
  const subjects = item.Subject || item.subjects || [];
  const hit = STATUS_TAGS.find(([subject]) => subjects.includes(subject));
  // sem tag de status conhecida, consulta antiga conta como encerrada
  return hit ? hit[1] : 'encerradas';
};

const rotuloDe = (statusKey) =>
  STATUS_TAGS.find(([, key]) => key === statusKey)?.[2] || 'Encerrada';

const ConsultationSearchView = ({ data, id, block }) => {
  const blockId = id || block;
  const dispatch = useDispatch();
  const source = sourcesOf(data)[0];

  const consultas = useSelector(
    (s) =>
      s.search?.subrequests?.[
        `agsusConsultationSearch_${blockId}_consultas`
      ] || {},
    shallowEqual,
  );

  React.useEffect(() => {
    // SSR já traz os dados via getAsyncData; este dispatch cobre navegação
    // client-side e mudança de fonte no editor.
    dispatch(
      searchContent(
        source.path,
        searchOptions(source),
        `agsusConsultationSearch_${blockId}_consultas`,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, blockId, source.path]);

  const [query, setQuery] = React.useState('');
  const [status, setStatus] = React.useState('todas');

  const loading = Boolean(consultas.loading);
  const items = consultas.items || [];

  const counts = items.reduce(
    (result, item) => ({
      ...result,
      [statusOf(item)]: (result[statusOf(item)] || 0) + 1,
    }),
    { abertas: 0, encerradas: 0 },
  );

  const visibleItems = items.filter(
    (item) =>
      (status === 'todas' || statusOf(item) === status) &&
      normalize(`${item.title || ''} ${item.description || ''}`).includes(
        normalize(query),
      ),
  );

  return (
    <section className="ag-consultation-search">
      <div className="ag-consultation-search__filters">
        <label>
          <span>Situação selecionada:</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="todas">Todas ({items.length})</option>
            <option value="abertas">Abertas ({counts.abertas})</option>
            <option value="encerradas">
              Encerradas ({counts.encerradas})
            </option>
          </select>
        </label>
        <label className="ag-consultation-search__query">
          <span>Pesquisar</span>
          <input
            type="search"
            value={query}
            placeholder="Digite uma palavra-chave"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>
      <div className="ag-consultation-search__table">
        <h2>{data.title || 'Consultas públicas'}</h2>
        {visibleItems.map((item) => (
          <UniversalLink key={item['@id']} href={flattenToAppURL(item['@id'])}>
            <span>
              <strong>{item.title}</strong>
              {item.description && <small>{item.description}</small>}
            </span>
            <span className="ag-consultation-search__meta">
              <span>{formatDate(item.effective)}</span>
              <mark>{rotuloDe(statusOf(item))}</mark>
            </span>
          </UniversalLink>
        ))}
        {!loading && !visibleItems.length && (
          <p className="ag-consultation-search__empty">
            Nenhuma consulta pública encontrada.
          </p>
        )}
        {loading && items.length === 0 && (
          <p className="ag-consultation-search__empty">
            Carregando consultas...
          </p>
        )}
      </div>
      <p className="ag-consultation-search__result" aria-live="polite">
        {`${visibleItems.length} ${visibleItems.length === 1 ? 'consulta' : 'consultas'}`}
      </p>
    </section>
  );
};

export default ConsultationSearchView;
